/**
 * ShortAppPreload is a lightweight alternative to AppPreload, designed for pages
 * that require minimal app bootstrapping before rendering.
 *
 * It skips loading certain configurations such as map config, map layers,
 * and extent layer preferences, which are present in AppPreload.
 *
 * Use this when the full AppPreload is unnecessary — for example, routes
 * like camera settings or wherever map level context is not required.
 *
 * Key Differences:
 * - Does not load map config (`useGetDefaultMapConfigQuery`)
 * - Does not load map layer configs (`useGetMapLayerConfigsQuery`)
 * - Does not load extent layer preferences (`useUserTenantPreference`)
 * - Has a simpler loading condition for faster app readiness
 *
 * This improves performance for routes where these configs are not needed.
 */

import { useGetOrgsQuery, useHasValidLicenseQuery } from '@kinetic-ui/data-layer';
import {
  FullscreenVideoPlayerProvider,
  PREFERENCE_KEYS,
  UserContext,
  useUserContext,
  useUserKineticPreference,
} from '@kinetic-ui/shared';
import React, { memo, Suspense, useEffect, useState } from 'react';
import { SelectedOrgPreference } from '../AppPreload/types';
import LoadingScreen from '../loaders/LoadingScreen';
import { ShortAppPreloadProps } from './types';
import { useInitializeTenant } from '../useInitializeTenant';

const ORG_ERROR = '[OrganizationError]';
/**
 * Load tenant and user configurations that we want to wait for before mounting the app
 */
const ShortAppPreload: React.FunctionComponent<ShortAppPreloadProps> = ({ children }) => {
  const userContextValue = useUserContext();
  const [loadComplete, setLoadComplete] = useState(false);
  const defaultOrgPreference = useUserKineticPreference<SelectedOrgPreference>({
    key: PREFERENCE_KEYS.DEFAULT_ORGANIZATION,
  });

  const orgsResult = useGetOrgsQuery();

  const { loading: licenseKeyLoading, error } = useHasValidLicenseQuery();

  useEffect(() => {
    if (error) {
      const orgError = error.graphQLErrors.find((error) => error.message.includes(ORG_ERROR));
      if (orgError) {
        window.sessionStorage.clear();
        window.localStorage.clear();
        window.location.reload();
      }
    }
  }, [error]);

  useInitializeTenant({ allOrganizations: orgsResult.data?.organizations, defaultOrgPreference });
  const { loading: defaultLoading } = defaultOrgPreference;
  // Add any extra loading variables here to validate we are done loading.
  const loadingAny = !![defaultLoading, orgsResult.loading, licenseKeyLoading].filter(Boolean)
    .length;

  useEffect(() => {
    if (!loadingAny) {
      setLoadComplete(true);
    }
  }, [defaultLoading, loadingAny, orgsResult.loading, userContextValue]);

  return (
    <UserContext.Provider value={userContextValue}>
      <FullscreenVideoPlayerProvider>
        <Suspense fallback={<LoadingScreen />}>
          {!loadComplete && <LoadingScreen />}
          {loadComplete && children}
        </Suspense>
      </FullscreenVideoPlayerProvider>
    </UserContext.Provider>
  );
};

export default memo(ShortAppPreload);
